import { Drawer, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { productWantRequestOperateLogList } from 'apis/product-want-request';
import showTotal from 'components/show-total';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LOCALS from 'commons/locals';
import { Trans, useTranslation } from 'react-i18next';
import { PmsProductWantRequestOperateLog } from 'types/pms-product-want-request';
import {
  formatWantRequestStatusChange,
  getWantRequestOperateTypeLabel,
} from './common';

type Props = {
  open: boolean;
  requestId?: number;
  onClose: () => void;
};

const OperateLogDrawer = ({ open, requestId, onClose }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<PmsProductWantRequestOperateLog[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadData = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const { list: logList, total: logTotal } =
        await productWantRequestOperateLogList(requestId, {
          pageNum,
          pageSize,
        });
      setList(logList);
      setTotal(logTotal);
    } finally {
      setLoading(false);
    }
  }, [requestId, pageNum, pageSize]);

  useEffect(() => {
    if (open && requestId) {
      loadData();
    }
  }, [open, requestId, loadData]);

  useEffect(() => {
    if (!open) {
      setPageNum(1);
      setPageSize(10);
      setList([]);
      setTotal(0);
    }
  }, [open]);

  const columns: ColumnsType<PmsProductWantRequestOperateLog> = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.operation_time} />,
        dataIndex: 'createTime',
        width: 170,
        render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: <Trans i18nKey={LOCALS.operate_type} />,
        dataIndex: 'operateType',
        width: 100,
        render: (type: PmsProductWantRequestOperateLog['operateType']) =>
          getWantRequestOperateTypeLabel(type, t),
      },
      {
        title: <Trans i18nKey={LOCALS.operator} />,
        dataIndex: 'operator',
        width: 120,
        render: (v?: string | null) => v || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.status} />,
        width: 160,
        render: (_, row) =>
          formatWantRequestStatusChange(row.fromStatus, row.toStatus, t),
      },
      {
        title: <Trans i18nKey={LOCALS.remark} />,
        dataIndex: 'remark',
        ellipsis: true,
        render: (v?: string | null) => v || '-',
      },
    ],
    [t],
  );

  return (
    <Drawer
      title={<Trans i18nKey={LOCALS.pwr_operate_log_title} />}
      open={open}
      onClose={onClose}
      width={760}
      destroyOnClose
    >
      <Table
        bordered
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={{
          showTotal,
          total,
          current: pageNum,
          pageSize,
          onChange: (page, size) => {
            setPageNum(page);
            setPageSize(size);
          },
        }}
      />
    </Drawer>
  );
};

export default OperateLogDrawer;
